import { computed, readonly, Ref } from 'vue';
import MarkdownIt from 'markdown-it';
import htmlToPdfMake from 'html-to-pdfmake';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useToast } from '@/composables/useToast';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { storeToRefs } from 'pinia';
import { assignFonts, cjkFontFor, loadFonts, requiredFonts } from '@/modules/viewer/composables/pdfFonts';
import { createMeasure, layoutRtl } from '@/modules/viewer/composables/pdfRtl';

pdfMake.vfs = pdfFonts;

const FONT_SIZE = 11;
const PAGE_MARGIN = 20;
// A4, pdfmake's default page size, in points.
const PAGE_WIDTH = 595.28;

const md = new MarkdownIt({ html: true, linkify: true, typographer: true });

const defaultStyles = {
  h1: { fontSize: 24, bold: true, marginBottom: 8 },
  h2: { fontSize: 22, bold: true, marginBottom: 6 },
  h3: { fontSize: 20, bold: true, marginBottom: 6 },
  h4: { fontSize: 18, bold: true, marginBottom: 6 },
  p: { margin: [0, 4, 0, 8] },
  ul: { margin: [0, 0, 0, 5], marginLeft: 15 },
  ol: { margin: [0, 0, 0, 5], marginLeft: 15 },
  strong: { bold: true },
};

const styleSheet = `h1{font-size:24px;font-weight:bold;margin-bottom:8px;}
h2{font-size:22px;font-weight:bold;margin-bottom:6px;}
h3{font-size:20px;font-weight:bold;margin-bottom:6px;}
h4{font-size:18px;font-weight:bold;margin-bottom:6px;}
p{margin:0 0 8px 0;}
ul,ol{margin:0 0 5px 0;padding-left:15px;}
strong{font-weight:bold;}`;

function htmlToPlain(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent?.trim() ?? '';
}

function stripImageMarkdown(text: string): string {
  return text.replace(/!\[[^\]]*]\([^)]*\)/g, '').trim();
}

const IMAGE_TYPES_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
};

// Screenshot artifacts uploaded before SW-6074 are served as application/octet-stream, and
// readAsDataURL copies that type into the data URL, so retype them from the file extension.
function asImageBlob(blob: Blob, url: string): Blob {
  if (blob.type.startsWith('image/')) {
    return blob;
  }
  const extension = new URL(url).pathname.split('.').pop()?.toLowerCase() ?? '';
  const type = IMAGE_TYPES_BY_EXTENSION[extension];
  if (!type) {
    throw new Error(`Not an image: ${blob.type}`);
  }
  return new Blob([blob], { type });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function inlineImages(wrapper: HTMLElement): Promise<HTMLImageElement[]> {
  const imgs = Array.from(wrapper.querySelectorAll<HTMLImageElement>('img'));
  const failed: HTMLImageElement[] = [];
  await Promise.all(
    imgs.map(async img => {
      try {
        // The viewer already loaded this URL through a plain <img>, so the browser cache holds a
        // response without CORS headers. Reusing it for this CORS fetch fails, so bypass the cache.
        const resp = await fetch(img.src, { cache: 'reload' });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        img.src = await blobToDataUrl(asImageBlob(await resp.blob(), img.src));
      } catch {
        failed.push(img);
      }
    })
  );
  return failed;
}

function removeImage(img: HTMLImageElement): void {
  const link = img.parentElement;
  const isOnlyChildOfLink = link?.tagName === 'A' && link.childNodes.length === 1;
  (isOnlyChildOfLink ? link : img).remove();
}

function omittedImagesMessage(count: number): string {
  if (count === 1) {
    return '1 screenshot could not be loaded and was left out of the PDF';
  }
  return `${count} screenshots could not be loaded and were left out of the PDF`;
}

export interface CopyHtmlFragmentOptions {
  linkToSmartAction?: boolean;
  // Replaces the rendered text as the text/plain flavour, for callers that
  // want the Markdown source (with its image links) when pasted as plain text.
  plainText?: string;
}

export interface ExportUtils {
  copyConfluenceMarkdown(): Promise<void>;
  copyMarkdownWithoutImages(): Promise<void>;
  copyHtmlFragment(markdown?: string, options?: CopyHtmlFragmentOptions): Promise<void>;
  downloadPdf(): Promise<void>;
  copyWithVideoLink(): Promise<void>;
  getVideoLink(linkToSmartAction?: boolean): string;
}

export function useExportUtils(description: Ref<string | undefined>, displayName: Ref<string | undefined>): ExportUtils {
  const rawHtml = readonly(computed(() => md.render(description.value ?? '')));
  const { success, info, warn, error } = useToast();
  const viewerItemStore = useViewerItemStore();
  const { shareLink } = storeToRefs(viewerItemStore);
  const smartActionsStore = useSmartActionsStore();
  const { actionType } = storeToRefs(smartActionsStore);

  function getVideoLink(linkToSmartAction = true): string {
    if (!shareLink.value) return '';
    const url = new URL(shareLink.value);
    const sa = linkToSmartAction ? actionType?.value || '' : '';
    if (sa) {
      url.searchParams.set('sa', sa);
    }
    return `See the full video: ${url.toString()}`;
  }

  // Video links normally reopen the viewer on the active smart action's summary.
  // Pass false when the copied text isn't that summary, e.g. a single to-do.
  async function copyHtmlFragment(markdown?: string, { linkToSmartAction = true, plainText: plainBody }: CopyHtmlFragmentOptions = {}): Promise<void> {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = markdown === undefined ? rawHtml.value : md.render(markdown);
    await inlineImages(wrapper);

    const htmlContent = `<div>${wrapper.innerHTML}</div><div style="margin-top:1em;">${getVideoLink(linkToSmartAction)}</div>`;

    const htmlFragment = `<style>${styleSheet}</style>${htmlContent}`;
    const plainText = (plainBody ?? htmlToPlain(wrapper.innerHTML)) + `\n\n${getVideoLink(linkToSmartAction)}`;

    try {
      const blobHtml = new Blob([htmlFragment], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });
      const item = new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText,
      });
      await navigator.clipboard.write([item]);
    } catch {
      // fallback if ClipboardItem unsupported
      await navigator.clipboard.writeText(plainText);
    }

    success('Content copied to clipboard');
  }

  async function downloadPdf(): Promise<void> {
    info('Preparing PDF…');
    const wrapper = document.createElement('div');
    wrapper.innerHTML = rawHtml.value;
    const failedImages = await inlineImages(wrapper);
    failedImages.forEach(removeImage);

    wrapper.querySelectorAll<HTMLImageElement>('img').forEach(img => {
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.style.maxWidth = `700px`;
      img.style.height = 'auto';
    });

    try {
      const text = wrapper.textContent ?? '';
      const scriptFonts = requiredFonts(text);
      const { fonts, vfs } = await loadFonts(scriptFonts);
      const content = assignFonts(
        htmlToPdfMake(wrapper.innerHTML, {
          window: globalThis,
          defaultStyles,
          removeExtraBlanks: true,
        }),
        cjkFontFor(text)
      );
      const hasArabic = fonts && vfs && scriptFonts.includes('NotoSansArabic');

      pdfMake
        .createPdf(
          {
            content: hasArabic ? layoutRtl(content, createMeasure({ fonts, vfs }), PAGE_WIDTH - 2 * PAGE_MARGIN, FONT_SIZE) : content,
            defaultStyle: { fontSize: FONT_SIZE },
            pageMargins: [PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN, PAGE_MARGIN],
          },
          undefined,
          fonts,
          vfs
        )
        .download(`${displayName.value}.pdf`);
    } catch {
      error('The PDF could not be generated. Please try again.');
      return;
    }

    if (failedImages.length > 0) {
      warn(omittedImagesMessage(failedImages.length));
    }
  }

  async function copyMarkdownWithoutImages(): Promise<void> {
    const mdText = stripImageMarkdown(description.value ?? '');
    const result = mdText + `\n\n${getVideoLink()}`;
    await navigator.clipboard.writeText(result);
    success('Content copied to clipboard');
  }

  function addImageBullets(markdown: string): string {
    return markdown
      .split(/\r?\n/)
      .map(line => {
        if (/^\s*-\s*!\[/.test(line)) {
          return line;
        }
        if (/^\s*!\[/.test(line)) {
          return line.replace(/^\s*/, indent => `${indent}- `);
        }
        return line;
      })
      .join('\n');
  }

  function flattenNestedImageLinks(markdown: string): string {
    // Convert [![alt](imgSrc)](linkHref) → ![alt](imgSrc)\n\n[View full-size image](linkHref)
    return markdown.replace(/\[!\[([^\]]*)\]\(([^)]*)\)\]\(([^)]*)\)/g, '![$1]($2)\n\n[View full-size image]($3)');
  }

  async function copyConfluenceMarkdown(): Promise<void> {
    const raw = description.value ?? '';
    const flattened = flattenNestedImageLinks(raw);
    const withBullets = addImageBullets(flattened);
    const result = withBullets + `\n\n${getVideoLink()}`;
    await navigator.clipboard.writeText(result);
    success('Content copied to clipboard');
  }

  async function copyWithVideoLink(): Promise<void> {
    const content = description.value || '';
    const videoLink = getVideoLink();
    const result = content + (videoLink ? `\n\n${videoLink}` : '');

    try {
      await navigator.clipboard.writeText(result);
      success('Content copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }

  return {
    copyConfluenceMarkdown,
    copyMarkdownWithoutImages,
    copyHtmlFragment,
    downloadPdf,
    copyWithVideoLink,
    getVideoLink,
  };
}
